import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Stack, Grid, Chip, CircularProgress, Alert,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Cloud, Trees, Zap, HelpCircle, ChevronDown, MapPin, Compass, Sparkles, Leaf } from 'lucide-react';
import { getRetreatAdditionalInfo } from '../../../Services/Retreats/RetreatApis';

// ─── Shared primitives ────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

const HtmlContent = ({ html }) => (
    <Box
        sx={{
            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
            '& p': { mb: 1 },
            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

const cardPx = { xs: 2, sm: 2.5 };

/** A sub-section row inside Environmental card */
const EnvRow = ({ icon: Icon, iconColor, label, html, noBorder }) => (
    <Box sx={{ py: 1.5, borderBottom: noBorder ? 'none' : '1px solid #F1F5F9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Icon size={13} color={iconColor} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </Typography>
        </Box>
        <HtmlContent html={html} />
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AdditionalInfoTab = ({ retreat }) => {
    const [retreatAdditionalInfo, setRetreatAdditionalInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedFAQ, setExpandedFAQ] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatAdditionalInfo(retreat.id);
                if (response?.success) {
                    setRetreatAdditionalInfo(response.data);
                } else {
                    setError('Failed to fetch additional information.');
                }
            } catch (err) {
                console.error('Error fetching retreat additional info:', err);
                setError('An error occurred while fetching additional information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    const handleFAQToggle = (id) => setExpandedFAQ(expandedFAQ === id ? null : id);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;

    if (!retreatAdditionalInfo) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No additional information available.</Typography>
            </InfoCard>
        );
    }

    const info = retreatAdditionalInfo.additionalInfo;
    const hasEnvInfo = info && (
        info.seasonalInformation || info.weatherInformation ||
        info.electricityInformation || info.electricityAndOutlets ||
        info.responsibleTourismTips
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Environmental & Practical Information ──────────────────── */}
            {hasEnvInfo && (
                <Box>
                    <SectionTitle icon={Cloud}>Environmental & Practical Information</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, pt: 0.5, pb: 0.5 }}>
                            {info.seasonalInformation && (
                                <EnvRow icon={Trees} iconColor="#10B981" label="Seasonal Information" html={info.seasonalInformation} />
                            )}
                            {info.weatherInformation && (
                                <EnvRow icon={Cloud} iconColor="#3B82F6" label="Weather Information" html={info.weatherInformation} />
                            )}
                            {(info.electricityAndOutlets || info.electricityInformation) && (
                                <EnvRow
                                    icon={Zap} iconColor="#F59E0B"
                                    label="Electricity & Outlets"
                                    html={info.electricityAndOutlets || info.electricityInformation}
                                />
                            )}
                            {info.responsibleTourismTips && (
                                <EnvRow
                                    icon={Leaf} iconColor="#059669"
                                    label="Responsible Tourism Tips"
                                    html={info.responsibleTourismTips}
                                    noBorder
                                />
                            )}
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 2. Surrounding Areas / Things / Facilities ───────────────── */}
            {(retreatAdditionalInfo.surroundingAreas?.length > 0 ||
              retreatAdditionalInfo.thingsInArea?.length > 0 ||
              retreatAdditionalInfo.facilitiesOptions?.length > 0) && (
                <Grid container spacing={2}>
                    {/* Surrounding Areas */}
                    {retreatAdditionalInfo.surroundingAreas?.length > 0 && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <SectionTitle icon={Compass}>Surrounding Areas</SectionTitle>
                            <InfoCard sx={{ height: 'calc(100% - 38px)' }}>
                                <Box sx={{ px: cardPx, py: 1.5 }}>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                        {retreatAdditionalInfo.surroundingAreas.map((area) => (
                                            <Chip
                                                key={area.id}
                                                label={area.surroundingAreaName}
                                                size="small"
                                                icon={<MapPin size={11} color="#1D4ED8" />}
                                                sx={{
                                                    height: 24, fontSize: '0.73rem', fontWeight: 500,
                                                    bgcolor: '#EFF6FF', color: '#1D4ED8',
                                                    border: '1px solid #BFDBFE', borderRadius: '6px',
                                                    '& .MuiChip-icon': { color: 'inherit' },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </InfoCard>
                        </Grid>
                    )}

                    {/* Things In Area */}
                    {retreatAdditionalInfo.thingsInArea?.length > 0 && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <SectionTitle icon={Sparkles}>Things In Area</SectionTitle>
                            <InfoCard sx={{ height: 'calc(100% - 38px)' }}>
                                <Box sx={{ px: cardPx, py: 1.5 }}>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                        {retreatAdditionalInfo.thingsInArea.map((thing) => {
                                            const name = thing.thingsInAreaName || thing.ownThingsInArea;
                                            if (!name) return null;
                                            return (
                                                <Chip
                                                    key={thing.id}
                                                    label={name}
                                                    size="small"
                                                    icon={<Sparkles size={11} color="#047857" />}
                                                    sx={{
                                                        height: 24, fontSize: '0.73rem', fontWeight: 500,
                                                        bgcolor: '#ECFDF5', color: '#047857',
                                                        border: '1px solid #A7F3D0', borderRadius: '6px',
                                                        '& .MuiChip-icon': { color: 'inherit' },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </InfoCard>
                        </Grid>
                    )}

                    {/* Facilities Options */}
                    {retreatAdditionalInfo.facilitiesOptions?.length > 0 && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <SectionTitle icon={Zap}>Facilities Options</SectionTitle>
                            <InfoCard sx={{ height: 'calc(100% - 38px)' }}>
                                <Box sx={{ px: cardPx, py: 1.5 }}>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                        {retreatAdditionalInfo.facilitiesOptions.map((facility) => {
                                            const name = facility.facilitiesOptionName || facility.ownFacilities;
                                            if (!name) return null;
                                            return (
                                                <Chip
                                                    key={facility.id}
                                                    label={name}
                                                    size="small"
                                                    icon={<Zap size={11} color="#B45309" />}
                                                    sx={{
                                                        height: 24, fontSize: '0.73rem', fontWeight: 500,
                                                        bgcolor: '#FFFBEB', color: '#B45309',
                                                        border: '1px solid #FDE68A', borderRadius: '6px',
                                                        '& .MuiChip-icon': { color: 'inherit' },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </InfoCard>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* ── 3. FAQs ───────────────────────────────────────────────────── */}
            {retreatAdditionalInfo.faqs?.length > 0 && (
                <Box>
                    <SectionTitle icon={HelpCircle}>Frequently Asked Questions</SectionTitle>
                    <Stack spacing={1}>
                        {retreatAdditionalInfo.faqs.map((faq) => (
                            <Accordion
                                key={faq.id}
                                expanded={expandedFAQ === faq.id}
                                onChange={() => handleFAQToggle(faq.id)}
                                elevation={0}
                                sx={{
                                    border: '1px solid #E8EEF4',
                                    borderRadius: '10px !important',
                                    bgcolor: '#FFFFFF',
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': { margin: 0 },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ChevronDown size={16} color="#94A3B8" />}
                                    sx={{ px: cardPx, py: 0.5, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{ px: cardPx, py: 1.5, borderTop: '1px solid #F1F5F9', bgcolor: '#FAFBFC' }}
                                >
                                    <HtmlContent html={faq.answer || ''} />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            )}

        </Box>
    );
};

export default AdditionalInfoTab;
